/**
 * Disposal Inbox — pending disposal requests awaiting approval
 * Authority: ASSET_DISPOSE_APPROVE | ASSET_MANAGE
 * Backend: GET /api/hr/assets/disposals/inbox
 *          POST /api/hr/assets/disposals/{id}/approve
 */
import { useState, useCallback } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import { assetApi } from "../api";
import type { AssetDisposal, DisposalApproveRequest } from "../types";
import { useAsyncData, usePagination } from "../hooks";
import {
  AssetError, AssetLoading, BackBtn, Btn, Card, CardHeader,
  PageHeader, Pagination, Table, TBody, TD, TH, THead, TR,
  fmtDate,
} from "../components/AssetUI";

type ModalState =
  | { mode: "approve"; disposal: AssetDisposal }
  | { mode: "reject";  disposal: AssetDisposal }
  | null;

export default function AssetDisposalInboxPage() {
  const pagination = usePagination();
  const [modal, setModal] = useState<ModalState>(null);
  const [comments, setComments]         = useState("");
  const [rejectionReason, setRejection] = useState("");

  const { data, loading, error, reload } = useAsyncData(
    () => assetApi.listDisposalInbox(pagination.page, pagination.size),
    [pagination.page, pagination.size],
  );

  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const submit = useCallback(async () => {
    if (!modal) return;
    setSaving(true);
    setSaveError(null);
    try {
      const req: DisposalApproveRequest = modal.mode === "approve"
        ? { approved: true,  comments: comments || undefined }
        : { approved: false, comments: comments || undefined, rejectionReason };
      await assetApi.approveOrRejectDisposal(modal.disposal.id, req);
      setModal(null);
      setComments("");
      setRejection("");
      reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }, [modal, comments, rejectionReason, reload]);

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Asset Management"
        title="Disposal Inbox"
        actions={<BackBtn to="/assets" label="Dashboard" />}
      />

      {loading && <AssetLoading label="Loading…" />}
      {error   && <AssetError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Card noPad>
            <CardHeader
              title="Pending Disposal Requests"
              subtitle={`${data.totalElements} request${data.totalElements === 1 ? "" : "s"} awaiting review`}
            />
            <Table>
              <THead>
                <TH>Asset</TH>
                <TH>Reason</TH>
                <TH>Method</TH>
                <TH>Requested By</TH>
                <TH>Requested</TH>
                <TH>Notes</TH>
                <TH right>Actions</TH>
              </THead>
              <TBody>
                {data.content.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-sm text-slate-400">
                      No pending disposal requests.
                    </td>
                  </tr>
                ) : data.content.map((d) => (
                  <TR key={d.id}>
                    <TD>
                      <div className="font-semibold text-slate-800">{d.assetName}</div>
                      <div className="font-mono text-xs text-slate-400">{d.assetNumber ?? "—"}</div>
                    </TD>
                    <TD muted><span className="line-clamp-2 max-w-xs text-xs">{d.reason}</span></TD>
                    <TD muted><span className="text-xs">{d.disposalMethod?.replace(/_/g, " ") ?? "—"}</span></TD>
                    <TD muted>{d.requestedBy ?? "—"}</TD>
                    <TD muted>{fmtDate(d.requestedAt)}</TD>
                    <TD muted><span className="line-clamp-2 max-w-xs text-xs">{d.notes ?? "—"}</span></TD>
                    <TD right>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setModal({ mode: "approve", disposal: d })}
                          className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition"
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => setModal({ mode: "reject", disposal: d })}
                          className="inline-flex items-center gap-1.5 rounded-xl bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Reject
                        </button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}

      {/* Approve / Reject modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="mb-1 text-lg font-bold text-slate-800">
              {modal.mode === "approve" ? "Approve Disposal" : "Reject Disposal"}
            </h2>
            <p className="mb-4 text-sm text-slate-500">
              Asset: <span className="font-semibold text-slate-700">{modal.disposal.assetName}</span>
            </p>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Comments (optional)
                </label>
                <textarea
                  rows={2}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Any comments on this decision…"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              {modal.mode === "reject" && (
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={3}
                    value={rejectionReason}
                    onChange={(e) => setRejection(e.target.value)}
                    placeholder="Explain why the request is being rejected…"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
              )}

              {saveError && (
                <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{saveError}</p>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <Btn variant="ghost" size="md" onClick={() => { setModal(null); setComments(""); setRejection(""); }}>
                Cancel
              </Btn>
              <Btn
                variant={modal.mode === "approve" ? "primary" : "danger"}
                size="md"
                loading={saving}
                disabled={modal.mode === "reject" && !rejectionReason.trim()}
                onClick={submit}
              >
                {modal.mode === "approve" ? "Confirm Approval" : "Confirm Rejection"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
